interface Grade {
  name: string;
}

// Cache for normalized grade names
const gradeNameCache = new Map<string, string>();

/**
 * Normalizes a grade name to a standardized format
 * e.g., "Kindergarten" -> "K", "1st Grade" -> "1"
 */
function normalizeGradeName(name: string): string {
  if (gradeNameCache.has(name)) {
    return gradeNameCache.get(name)!;
  }

  let result: string;
  if (name.toLowerCase().includes('kindergarten') || name === '0') {
    result = 'K';
  } else {
    const numericMatch = name.match(/\d+/);
    if (numericMatch) {
      result = numericMatch[0];
    } else {
      result = name;
    }
  }

  gradeNameCache.set(name, result);
  return result;
}

// Cache for formatted grade displays
const gradeDisplayCache = new WeakMap<Grade[], string>();

/**
 * Formats an array of grades into a human-readable string
 * e.g., [{name: "Kindergarten"}, {name: "1st Grade"}, {name: "2nd Grade"}] -> "K-2"
 * @param grades Array of grade objects with name property
 * @returns Formatted grade range string
 */
export function formatGradeDisplay(grades?: Grade[]): string {
  if (!grades || grades.length === 0) return 'N/A';

  if (gradeDisplayCache.has(grades)) {
    return gradeDisplayCache.get(grades)!;
  }

  const formattedGrades = grades
    .map((grade) => normalizeGradeName(grade.name))
    .sort((a, b) => {
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });

  const result = formattedGrades.length === 1
    ? formattedGrades[0]
    : `${formattedGrades[0]}-${formattedGrades[formattedGrades.length - 1]}`;

  gradeDisplayCache.set(grades, result);
  return result;
} 